import React from 'react';
const HelpPage = () => {

  // Seções com passos numerados e exemplos
  const helpSections = [
    {
      title: 'Dashboard',
      description: 'Visão geral do sistema com métricas principais e gráficos de desempenho. Acompanhe o status em tempo real e atualize os dados quando necessário.',
      steps: [
        'Acesse o menu a esquerda → Dashboard',
        "Clique no botão 'Atualizar' para buscar os dados mais recentes.",
        'Leia as métricas: Total de Contatos, Interações, Intervenções Humanas e Documentos.',
        'Passe o mouse sobre o gráfico de Interações Diárias para ver os valores exatos.',
        'Interprete tendências: picos indicam maior atividade; muitas intervenções humanas sugerem ajustar a IA.'
      ],
      examples: [
        'Intervenções Humanas significa que a IA chamou o atendente humano por não saber responder a pergunta. Isso indica que a informação não está presente nos documentos.',
        'Se alguma informação estiver disconexa avise-nos diretamente para que correções sejam realizadas.',
        'Uma boa média de "Intervenções Humanas" é 5~10%, se for maior, revise os documentos e a IA.'
      ]
    },
    {
      title: 'Gerenciar IA',
      description: 'Configure a IA: ligar/desligar, definir persona, enviar imagem usada no script de boas-vindas e limpar cache para refletir mudanças nas conversas.',
      steps: [
        'Acesse o menu → Gerenciar IA',
        'Ligue ou desligue a IA pelos botões correspondentes (Ativar/Desligar).',
        'Configure a persona: nome, descrição e script de recepção.',
        'Faça upload de imagem (PNG/JPG) e verifique o preview antes de salvar.',
        'Clique em Salvar para aplicar as mudanças e aguarde a notificação.',
        'Use Limpar Cache para invalidar respostas antigas e refletir novas configurações.'
      ],
      examples: [
        'SEMPRE que a IA apresentar algum problema, desligue-a e depois nos contate. Isso evita que ela gere mais problemas até que seja realizado o troubleshooting.',
        'A imagem usada no script de recepção é exibida no preview antes de ser salva. Se a imagem não estiver aparecendo, verifique o formato (PNG/JPG) e o tamanho (200x200px).',
        'A imagem é usada apenas no script de recepção e não afeta o funcionamento da IA.'
      ]
    },
    {
      title: 'Agendamento IA',
      description: 'Defina horários e regras para o funcionamento automatizado da IA. Ative, pause e ajuste a grade de horários conforme sua operação.',
      steps: [
        'Acesse o menu → Agendamento IA.',
        'Verifique o status: Sistema Ativo/Inativo e ajuste conforme necessário.',
        'Defina horários disponíveis por dia usando a grade de 24 horas.',
        'Configure regras de atendimento (intervalos, limites e comportamento).',
        'Salve o agendamento e confirme as alterações.'
      ],
      examples: [
        'Ex: Configure atendimento comercial das 09h às 18h em dias úteis.',
        'Ex: Pause o agendamento em feriados.'
      ]
    },
    {
      title: 'Documentos',
      description: 'Faça upload, organize, pesquise e gerencie documentos usados pela plataforma e pela IA.',
      steps: [
        'Acesse o menu → Documentos.',
        'Clique em Upload e selecione arquivos (PDF, DOCX, etc.).',
        'Organize por categorias/pastas e aplique tags quando disponíveis.',
        'Use a barra de busca e filtros para localizar documentos.',
        'Baixe ou exclua documentos conforme necessário.',
        'Clique em Atualizar/Refresh para recarregar a lista.'
      ],
      examples: [
        'Ex: Suba contratos e políticas para a IA referenciar em conversas.',
        'Ex: Filtre por tag para revisar documentos de uma campanha específica.'
      ]
    },
    {
      title: 'Contatos',
      description: 'Visualize, filtre, exporte e gerencie bloqueios de IA por contato. Acompanhe estatísticas e use paginação para navegação.',
      steps: [
        'Acesse o menu → Contatos.',
        'Visualize a lista e as estatísticas carregadas do seu banco (target_db_name).',
        'Use o campo de filtro por nome ou telefone.',
        'Navegue pelas páginas usando a paginação.',
        'Exporte contatos em CSV ou DOCX quando necessário.',
        'Bloqueie/Desbloqueie a IA por contato usando o ícone de escudo (Shield/ShieldOff).'
      ],
      examples: [
        'Ex: Bloqueie IA para números internos ou VIPs que exigem atendimento humano.',
        'Ex: Exporte a base para planilhas e relatórios externos.'
      ]
    },
    {
      title: 'Analytics',
      description: 'Analise métricas avançadas: interações, tempo de resposta e intervenções. Use o gráfico dos últimos 7 dias para avaliar tendências.',
      steps: [
        'Acesse o menu → Analytics.',
        "Clique em 'Atualizar' para carregar dados recentes (refresh com debounce).",
        'Leia os cartões com Total de Interações, Tempo de Resposta e Intervenções Humanas.',
        'Analise o gráfico de Interações Diárias (últimos 7 dias).',
        'Interprete tendências e ajuste scripts/campanhas conforme os resultados.'
      ],
      examples: [
        'Ex: Tempo de resposta alto indica revisar integrações e fluxos.',
        'Ex: Queda nas interações pode sinalizar necessidade de nova campanha.'
      ]
    },
    {
      title: 'Conectar WhatsApp',
      description: 'Conecte sua instância WhatsApp: escaneie o QR, verifique o status, configure webhook e controle a sessão.',
      steps: [
        'Acesse o menu → Conectar WhatsApp.',
        'Crie ou selecione uma instância disponível.',
        'Escaneie o QR Code pelo WhatsApp para autenticar.',
        'Verifique o status (conectado/desconectado) e a sessão ativa.',
        'Gere e configure o webhook da instância conforme sua necessidade.',
        'Inicie/pare a instância e exclua quando necessário.'
      ],
      examples: [
        'Ex: Refaça o processo de QR se a sessão expirar.',
        'Ex: Use o webhook para integrar mensagens recebidas ao seu back-end.'
      ]
    },
    {
      title: 'CRM',
      description: 'Gerencie clientes e campanhas. Crie segmentos, inicie/pausa campanhas e acompanhe resultados no painel CRM.',
      steps: [
        'Acesse o menu → CRM.',
        'Gerencie clientes (adicionar, editar, remover e segmentar).',
        'Crie campanhas com público e objetivo definidos.',
        'Inicie/pausa campanhas via CRMDashboard.',
        'Acompanhe métricas e resultados por campanha.'
      ],
      examples: [
        'Ex: Configure uma campanha de follow-up 3 dias após o primeiro contato.',
        'Ex: Segmente clientes por tags para campanhas específicas.'
      ]
    },
    {
      title: 'Usuários (Admin)',
      description: 'Administre usuários: criação, edição, permissões e status. Pesquise e acompanhe histórico quando disponível.',
      steps: [
        'Acesse o menu → Usuários (apenas administradores).',
        'Crie novos usuários com dados básicos.',
        'Edite permissões e papéis (roles) conforme responsabilidades.',
        'Ative/Desative contas conforme política de acesso.',
        'Use busca para localizar usuários e revisar histórico quando aplicável.'
      ],
      examples: [
        "Ex: Defina papel 'admin' para gestores de equipe.",
        'Ex: Desative contas inativas para segurança.'
      ]
    },
    {
      title: 'Administração (Admin)',
      description: 'Painel administrativo para configurações globais, logs e monitoramento. Algumas funcionalidades podem estar em desenvolvimento.',
      steps: [
        'Acesse o menu → Administração (apenas administradores).',
        'Ajuste parâmetros globais de sistema e integração quando disponíveis.',
        'Consulte logs e monitore o desempenho.',
        'Utilize ferramentas de manutenção e debug conforme necessário.'
      ],
      examples: [
        'Ex: Ajuste limites de uso da plataforma.',
        'Ex: Revise logs após incidentes de produção.'
      ]
    }
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-4xl font-extrabold font-headline tracking-tight text-on-surface">
          Central de Ajuda
        </h1>
        <p className="text-on-surface-variant font-medium font-body">
          Bem-vindo à central de ajuda do PapIA. Aqui você encontrará instruções passo a passo para cada página do sistema.
        </p>
      </div>

      {/* Quick Start */}
      <div className="glass-card glow-emerald rounded-xl border border-outline-variant/10 p-6">
        <h2 className="text-xl font-bold font-headline text-primary mb-3">
          Começando
        </h2>
        <p className="text-on-surface-variant font-body mb-4 text-sm">
          O PapIA é uma plataforma completa de gestão com IA integrada. Comece pelo Dashboard para ter uma visão geral e avance conforme sua necessidade.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-surface-container-highest rounded-lg p-4 border border-outline-variant/10">
            <h3 className="font-bold text-primary mb-2 text-sm uppercase tracking-wider">Primeiros Passos</h3>
            <ul className="space-y-1 text-sm text-on-surface-variant font-body">
              <li>• Conecte seu WhatsApp</li>
              <li>• Explore o Dashboard</li>
              <li>• Configure a IA</li>
            </ul>
          </div>
          <div className="bg-surface-container-highest rounded-lg p-4 border border-outline-variant/10">
            <h3 className="font-bold text-tertiary mb-2 text-sm uppercase tracking-wider">Dicas Importantes</h3>
            <ul className="space-y-1 text-sm text-on-surface-variant font-body">
              <li>• Reveja as configurações da IA</li>
              <li>• Verifique o status do agendamento</li>
              <li>• Atualize os documentos na página de documentos</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sections Grid */}
      <div className="grid gap-6">
        {helpSections.map((section, index) => (
          <div
            key={index}
            className="glass-card glow-emerald rounded-xl border border-outline-variant/10 p-6 transition-all duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold font-headline text-on-surface mb-1">
                  {section.title}
                </h3>
                <p className="text-on-surface-variant font-body text-sm">
                  {section.description}
                </p>
              </div>
              <div className="ml-4 px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                {section.steps.length} passos
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-surface-container-highest rounded-lg p-4 border border-outline-variant/10">
                <h4 className="font-bold text-on-surface text-sm mb-3">Passo a passo</h4>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-on-surface-variant font-body">
                  {section.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
              <div className="bg-surface-container-highest rounded-lg p-4 border border-outline-variant/10">
                <h4 className="font-bold text-on-surface text-sm mb-3">Dicas de Uso</h4>
                <ul className="space-y-2 text-sm text-on-surface-variant font-body">
                  {section.examples.map((ex, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Support Section */}
      <div className="glass-card glow-emerald rounded-xl border border-outline-variant/10 p-6">
        <h2 className="text-xl font-bold font-headline text-on-surface mb-2">Precisa de mais ajuda?</h2>
        <p className="text-on-surface-variant font-body text-sm mb-4">
          Se você tiver dúvidas específicas ou precisar de assistência adicional, entre em contato com nossa equipe de suporte.
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="p-4 rounded-lg border border-outline-variant/10 bg-surface-container-highest flex-1 min-w-[200px]">
            <h3 className="font-bold text-primary text-sm mb-1">Suporte Técnico</h3>
            <p className="text-xs text-on-surface-variant font-body">Para problemas técnicos e dúvidas sobre funcionalidades</p>
          </div>
          <div className="p-4 rounded-lg border border-outline-variant/10 bg-surface-container-highest flex-1 min-w-[200px]">
            <h3 className="font-bold text-tertiary text-sm mb-1">Documentação</h3>
            <p className="text-xs text-on-surface-variant font-body">Acesse documentação detalhada e tutoriais</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;